import assert from 'assert';
import dayjs from 'dayjs';
import { SYSTEM_DEVICE } from '../hooks/useSystemDevice';
import lottieBeachDateService from '../lottie/beach-date-service.json';
import lottieHome from '../lottie/home.json';
import lottieKiteCup from '../lottie/kite-cup.json';
import lottieKiteWorkshop from '../lottie/kite-workshop.json';
import lottieMudflatHiking from '../lottie/mudflat-hiking.json';
import lottieNightskyGazing from '../lottie/nightsky-gazing.json';
import lottiePhotoShooting from '../lottie/photo-shooting.json';
import lottieYogaSession from '../lottie/yoga-session.json';
import { EventInstanceModel } from './EventInstanceModel';

export class EventTypeModel {
  private static models = new Map<EventTypeKey, EventTypeModel>();

  private constructor(private _key: EventTypeKey) {}

  get attrs() {
    const result = localData.find(({ key }) => key === this.key);
    assert(result, `Local data not found for event type key: ${this.key}`);
    return result;
  }

  get events() {
    return EventInstanceModel.findAll()
      .filter((event) => {
        return event.typeId === this.attrs.id;
      })
      .sort((event1, event2) => {
        return event1.time.start.valueOf() - event2.time.start.valueOf();
      });
  }

  get fullName() {
    return this.attrs.fullName ?? this.attrs.name;
  }

  get key() {
    return this._key;
  }

  get linkPath() {
    return `/event/${this.key}`;
  }

  get recommendedEvents() {
    return this.attrs.recommendedEventsKeys.map(EventTypeModel.find);
  }

  get weekDays() {
    const allWeekdays = this.events.map((event) => {
      return dayjs(event.time.start).format('dd');
    });
    const uniqueWeekdays = allWeekdays.filter((weekDay, index, self) => {
      return self.indexOf(weekDay) === index;
    });
    return uniqueWeekdays.join('–');
  }

  static find(this: void, key: EventTypeKey) {
    let model = EventTypeModel.models.get(key);
    if (!model) {
      model = new EventTypeModel(key);
      EventTypeModel.models.set(key, model);
    }
    return model;
  }

  static findAll(this: void) {
    return localData.map(({ key }) => EventTypeModel.find(key));
  }

  static findById(this: void, id: string) {
    return EventTypeModel.findAll().find(({ attrs }) => attrs.id === id);
  }

  getImageSrc(type: EventImageType, device: SYSTEM_DEVICE) {
    return `${process.env.PUBLIC_URL}/media/events/${this.key}/${device}/${type}.jpg`;
  }
}

const eventTypeKeys = [
  'beach-date-service',
  'kite-cup',
  'kite-workshop',
  'mudflat-hiking',
  'nightsky-gazing',
  'ocean-cinema',
  'photo-shooting',
  'yoga-session',
] as const;

export type EventTypeKey = (typeof eventTypeKeys)[number];

export function isValidEventKey(key: unknown): key is EventTypeKey {
  return eventTypeKeys.includes(key as EventTypeKey);
}

export interface EventTypeAttributes {
  animationData: unknown;
  displayInfoBeforeRegistration?: boolean;
  duration: string;
  extraInfo?: {
    subtitle?: string;
    text: string | string[];
    title: string;
  };
  fullName?: string;
  hasExtraImage?: boolean;
  headerTextBottom?: string;
  headerTextTop?: string;
  hideRegistration?: boolean;
  id: string;
  info?: {
    text: string | string[];
    title: string;
  };
  instructor?: {
    firstName: string;
    intro: string;
    lastName: string;
  };
  key: EventTypeKey;
  location?: string;
  mapInfo?: {
    text: string | string[];
    title: string;
  };
  meetingPoint: string;
  name: string;
  positionOnMap: {
    bottom: number;
    left: number;
  };
  recommendedEventsKeys: EventTypeKey[];
}

export type EventImageType = 'landscape' | 'portrait' | 'square';

const localData: EventTypeAttributes[] = [
  {
    animationData: lottieBeachDateService,
    duration: '3h',
    id: '5cae9d4a71300b2c1b7aa3a6f8e6f83ccccc96617bcee458957c6fded7ee4f8a',
    info: {
      text: [
        'Freuen Sie sich auf unvergessliche Momente mit dem Beach Date am Weststrand von Sylt und nehmen Sie sich Zeit für Ihre Lieben. Mit einem Defender werden Sie samt eines vorbereiteten Picknick-Korbs zu Ihrem Ziel gebracht. Dort erwartet Sie Ihr exklusiver Strandkorb, in dem Sie den Tag in Ruhe ausklingen lassen können. Genießen Sie das sanfte Rauschen des Meeres und die warme Sonne, während Sie auf die Wellen der Nordsee blicken.',
      ],
      title: 'Für die ruhigen momente',
    },
    key: 'beach-date-service',
    location: 'Nord 1',
    mapInfo: {
      text: 'Ihr Beach Date findet am malerischen Weststrand von Sylt statt. Mit seinen weiten Sandstränden und der Aussicht auf die Nordsee ist dieser Ort der perfekte Rückzugsort, um einen entspannten Tag in Ruhe zu genießen.',
      title: 'Weststrand',
    },
    meetingPoint: 'Base Buhne16',
    name: 'Beach date service',
    positionOnMap: {
      bottom: 90,
      left: 30,
    },
    recommendedEventsKeys: [
      'photo-shooting',
      'ocean-cinema',
      'yoga-session',
      'mudflat-hiking',
    ],
  },
  {
    animationData: lottieKiteCup,
    duration: 'TBD',
    headerTextBottom: '',
    headerTextTop: '',
    hideRegistration: true,
    id: 'kite-cup',
    key: 'kite-cup',
    meetingPoint: 'Base Buhne16',
    name: 'Kitesurf World Cup',
    positionOnMap: {
      bottom: 44,
      left: 3,
    },
    recommendedEventsKeys: [
      'kite-workshop',
      'ocean-cinema',
      'nightsky-gazing',
      'yoga-session',
    ],
  },
  {
    animationData: lottieKiteWorkshop,
    duration: '1,5h',
    id: '5ef1d4596307ac4ba09981ff00e9dd60e6a81894f3b234a42b570839dfeefb5e',
    info: {
      text: 'Spüren Sie das Adrenalin auch außerhalb des Defender, wenn Sie mit Linus Erdmann, dem achtfacher, amtierender Deutscher Meister, auf den Wellen reiten. In kleiner Runde bauen Sie auf Ihre bestehenden Kiteboard-Grundlagen auf und verbringen Stunden voller Spannung und Vergnügen. Der Kitesurf Workshop vermittelt Ihnen wertvolle Praxiseinblicke und einzigartige Momente auf den Wellen Sylts.',
      title: 'Für die perfekte welle',
    },
    instructor: {
      firstName: 'Linus',
      lastName: 'Erdmann',
      intro:
        'Linus Erdmann ist als Kitesurf-Profi und achtfacher, amtierender Deutscher Meister wohl bekannt. Mit „Children of the Sea“ hat er 2015 zudem eine Plattform geschaffen, auf der Profis und Anfänger lernen und Erfahrungen austauschen. Im Workshop wird er Ihnen wertvolle Tipps und Techniken weitergeben, um auch Ihre Kitesurf-Fähigkeiten sicher auf das nächste Level zu bringen.',
    },
    key: 'kite-workshop',
    location: 'Nord 2',
    mapInfo: {
      title: 'Sylter Ellenbogen',
      text: 'Unser Kitesurf Workshop findet am Ellenbogen Sylts, dem idealen Ort für Wassersport statt. Fühlen Sie die Kraft des Windes und die Freiheit des Wassers. Tauchen Sie in die beeindruckende Küstenlandschaft ein. Lassen Sie sich von der natürlichen Schönheit Sylts und der Spannung des Kitesurfens mitreißen.',
    },
    meetingPoint: 'Base Buhne16',
    name: 'Kite workshop',
    positionOnMap: {
      bottom: 83,
      left: 68,
    },
    recommendedEventsKeys: [
      'kite-cup',
      'yoga-session',
      'nightsky-gazing',
      'photo-shooting',
    ],
  },
  {
    animationData: lottieMudflatHiking,
    duration: '3,5h',
    id: '5bbd82f7628f1110f9f0c5ce77dcdae8e97828cb0ce1741e5843100060623f60',
    info: {
      text: 'Entdecken Sie auf einer Wattwanderung die unglaubliche Welt des Wattenmeeres. Vom Strand „Buhne 16“ geht es mit dem Defender zur Kempener Vogelkoje, wo Ihre Wanderung beginnt. Unter der Leitung des erfahrenen Nationalpark-Wattführers Jan Krüger erkunden Sie das Naturschutzgebiet und lernen die Tier- und Pflanzenwelt dieses einzigartigen Biotops kennen.',
      title: 'Für den guten lauf',
    },
    instructor: {
      firstName: 'Jan',
      lastName: 'Krüger',
      intro:
        'Der auf Sylt geborene Jan Krüger ist ein Experte für das Wattenmeer und offizieller Nationalpark-Wattführer. Er wird Ihnen mit profundem Wissen und spielerischem Charme dieses einzigartige Ökosystem näherbringen und Ihnen dabei helfen, einen tiefgreifenden Einblick in diese Umgebung zu gewinnen.',
    },
    key: 'mudflat-hiking',
    location: 'Nordost',
    mapInfo: {
      title: 'Vogelkoje',
      text: 'Unsere Wattwanderung führt Sie zur Vogelkoje und zum östlich gelegenen Wattenmeer auf Sylt, einem atemberaubenden Naturschutzgebiet mit einer vielfältigen Vogelwelt und unvergleichlicher Schönheit.',
    },
    meetingPoint: 'Base Buhne16',
    name: 'Wattwandern',
    positionOnMap: {
      bottom: 72,
      left: 55,
    },
    recommendedEventsKeys: [
      'nightsky-gazing',
      'beach-date-service',
      'ocean-cinema',
      'photo-shooting',
    ],
  },
  {
    animationData: lottieNightskyGazing,
    duration: '3h',
    id: '91a4160f6b0060e515c4fb51c061d34c5648d3ed2aed6b51a78b7da358d23b04',
    info: {
      text: 'Erkunden Sie mit dem Defender die himmlischen Höhen bei unserem Nightsky Gazing. Starten Sie Ihren Abend in unserer Defender Lounge mit einer Verkostung der lokalen Sylter Spezialitäten. Weiter geht es zu den Morsumer Klippen, wo Sie unser Astronom mit seinen Teleskopen erwartet. Lassen Sie sich durch die funkelnde Sternennacht führen und seien Sie im August bereit für den atemberaubenden Perseiden Schauer. Halten Sie für diese Nächte am besten einige Wünsche bereit.',
      title: 'Für die magischen aussichten',
    },
    instructor: {
      firstName: 'Steffen',
      lastName: 'Jahrmarkt',
      intro:
        'Der Sylter Hobbyastronom Steffen Jahrmarkt bringt Ihnen die Faszination am Sternenhimmel näher: Blicken Sie gemeinsam mit ihm durch das Teleskop und erfahren Sie Faktisches und Philosophisches über unseren Kosmos und das Weltall. Je nach Sichtbedingungen beobachten Sie dabei den Mond, unsere Nachbarplaneten oder Galaxien. Im August ist die Chance auf Sternschnuppen besonders hoch, also: Wünsche mitbringen!',
    },
    key: 'nightsky-gazing',
    location: 'Ost',
    mapInfo: {
      title: 'Morsum kliff',
      text: 'Das Nightsky Gazing findet vor der malerischen Kulisse des Morsum Kliff auf Sylt statt. Hier erhalten Sie einen unverstellten Blick auf den funkelnden Sternenhimmel.',
    },
    meetingPoint: 'Base Buhne16',
    name: 'Nightsky gazing',
    positionOnMap: {
      bottom: 47,
      left: 60,
    },
    recommendedEventsKeys: [
      'ocean-cinema',
      'photo-shooting',
      'yoga-session',
      'mudflat-hiking',
    ],
  },
  {
    animationData: lottieHome,
    displayInfoBeforeRegistration: true,
    duration: '3h',
    extraInfo: {
      subtitle: 'Buhne 16',
      text: 'Die Buhne 16 ist der Startpunkt all Ihrer Defender Experience Aktivitäten. Ein Teil der Location wird während der Experience zur exklusiven Defender Lounge. Hier können Sie feine Speisen in entspannter Atmosphäre genießen oder sich auf Ihren bevorstehenden Ausflug freuen. Unabhängig von Ihrer Wahl, die Buhne 16 ist der ideale Ort, um Ihre Zeit auf Sylt in vollen Zügen zu genießen.',
      title: 'Für den ausklang',
    },
    hasExtraImage: true,
    id: '41768611c7c617f9fe2a1a4fc2a48955e108cc72188b66f44f1ed786b10f0afc',
    info: {
      title: 'Für die grossen gefühle',
      text: 'Am Abend geht es mit dem Defender zu einem exklusiven Dinner an der Buhne 16. Dort lassen Sie den Tag mit einem unvergesslichen Kinoerlebnis in den Dünen ausklingen. Genießen Sie den Sonnenuntergang mit der European Outdoor Film Tour und machen Sie es sich bequem für ein spannendes Kinoerlebnis unter freiem Himmel.',
    },
    key: 'ocean-cinema',
    location: 'Base Buhne16',
    fullName: 'Ocean cinema\nat Buhne 16',
    mapInfo: {
      title: 'Buhne 16',
      text: 'Die Defender Lounge und das Ocean Cinema liegen direkt an der Buhne 16, eingebettet in die Dünen von Kampen. Sie vereinen Eleganz und Komfort und bieten Ihnen einen angenehmen Rückzugsort vor und nach Ihren Aktivitäten.',
    },
    meetingPoint: 'Base Buhne16',
    name: 'Ocean cinema',
    positionOnMap: {
      bottom: 70,
      left: 13,
    },
    recommendedEventsKeys: [
      'nightsky-gazing',
      'mudflat-hiking',
      'kite-workshop',
      'beach-date-service',
    ],
  },
  {
    animationData: lottiePhotoShooting,
    duration: '4h',
    id: '6a9f370975f4ff54fd75d1fbe139b9bc027cd7c7d931b43ab6db83db46ad6030',
    info: {
      text: 'Entfalten Sie Ihre kreative Seite auf Sylt mit einem unvergesslichen Fotoshooting. Ihr Defender bringt Sie zur malerischen Hörnum Odde. Dort erwartet Sie ein professioneller Fotograf, der Sie und Ihre Lieben perfekt inszeniert – unvergessliche Urlaubsaufnahmen garantiert.',
      title: 'Für die ewigkeit',
    },
    instructor: {
      firstName: 'Roman',
      lastName: 'Matejov',
      intro:
        'Der in der Slowakei aufgewachsen und über zwanzig Jahren auf Sylt lebende Fotograf Roman Matejov hat vor 12 Jahren seine Leidenschaft zum Beruf gemacht. Seitdem hat er etwa eintausend Hochzeiten und Familien fotografisch begleitet, unzählige Menschen und Celebritys porträtiert sowie Fotoausstellungen organisiert. Seine Bilder wurden schon mehrfach in internationalen Fotowettbewerben ausgezeichnet und seine Bildsprache bindet echte Lebensmomente in kunstvolle Werke. Sie und Ihre Familie erwartet ein dynamisches Fotoshooting, eine Jagd nach den vergänglichen und kostbaren Augenblicken, und das alles in Verbindung mit der einmaligen sylter Landschaft.',
    },
    key: 'photo-shooting',
    location: 'Süd',
    mapInfo: {
      title: 'Hörnum odde',
      text: 'Mit seinen großartigen Dünen, der endlosen Küstenlandschaft und einem beeindruckenden Blick auf die Nordsee bietet die Hörnum Odde viele traumhafte Kulissen. Lassen Sie sich von der natürlichen Schönheit inspirieren und kreieren Sie einzigartige Erinnerungen, die für immer bleiben.',
    },
    meetingPoint: 'Base Buhne16',
    name: 'Photo shooting',
    positionOnMap: {
      bottom: 19,
      left: 0,
    },
    recommendedEventsKeys: [
      'yoga-session',
      'beach-date-service',
      'ocean-cinema',
      'mudflat-hiking',
    ],
  },
  {
    animationData: lottieYogaSession,
    duration: '3h',
    id: '30df39e2f9780678ab9421bb53d7640dc71b6f8ce32a78ef3f6aed34bac0d889',
    info: {
      text: 'Beginnen Sie Ihren Tag mit einer Yoga-Session an der malerischen Hörnum Odde auf Sylt. In einer kleinen Gruppe fahren wir Sie mit dem Defender zur Yoga-Location. Dort genießen Sie die beruhigende Atmosphäre und Harmonie der Natur; lassen Sie sich von der Morgensonne und dem Klang der Wellen inspirieren. Unsere Yogalehrerin begleiten Sie vor Ort durch vielseitige Übungen, die Körper und Geist stärken und entspannen. Anschließend bringen wir Sie zurück zur Buhne 16, wo Sie eine wohlverdiente Mittagspause in unserer Defender Lounge genießen können.',
      title: 'Für ausgeglichene momente',
    },
    instructor: {
      firstName: 'Birgit',
      lastName: 'Paulsen',
      intro:
        'Mit der gebürtigen Sylterin Birgit Paulsen finden Sie zu Ihrem inneren Yogi. Seit 2003 praktiziert sie vor allem Vinyasa Yoga, implementiert dabei aber auch Praktiken aus anderen Yogastilen, um mit Ihnen die perfekte Balance zu finden. Als zertifizierte Yogalehrerin für Jung und Alt schafft sie mit ihrer ruhigen Art und fachlichen Expertise eine entspannte Atmosphäre für alle – und vermittelt dabei tiefgreifendes Yoga-Wissen in der Praxis.',
    },
    key: 'yoga-session',
    location: 'Süd',
    mapInfo: {
      text: 'Die Yoga Session findet an der Hörnum Odde statt, einer idyllischen Dünenlandschaft auf Sylt mit endlosen Sandstränden und einem beeindruckenden Blick auf die Nordsee.',
      title: 'Hörnum Odde',
    },
    meetingPoint: 'Base Buhne16',
    name: 'Yoga session',
    positionOnMap: {
      bottom: 6,
      left: 18,
    },
    recommendedEventsKeys: [
      'photo-shooting',
      'kite-workshop',
      'beach-date-service',
      'nightsky-gazing',
    ],
  },
];
